import React, { useState, useEffect } from 'react';
import { X, Filter, Save, Trash2, Calendar, User, Tag } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface AdvancedFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterState) => void;
  currentFilters: FilterState;
}

export interface FilterState {
  search: string;
  status: string;
  priority: string;
  type: string;
  assigneeId: string;
  clientId: string;
  dueDateFrom: string;
  dueDateTo: string;
  createdDateFrom: string;
  createdDateTo: string;
  tags: string[];
  overdue: boolean;
  unassigned: boolean;
}

interface Client {
  id: string;
  displayName: string;
  legalName: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface SavedFilter {
  id: string;
  name: string;
  filters: FilterState;
  isDefault: boolean;
}

export const AdvancedFiltersModal: React.FC<AdvancedFiltersModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters
}) => {
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [showSaveFilter, setShowSaveFilter] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFilters(currentFilters);
      fetchClients();
      fetchUsers();
      fetchSavedFilters();
    }
  }, [isOpen, currentFilters]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      if (response.data.success) {
        setClients(response.data.data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/firm-admin/users');
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchSavedFilters = async () => {
    try {
      // This would be a new endpoint for saved filters
      const response = await api.get('/firm-admin/tasks/saved-filters');
      if (response.data.success) {
        setSavedFilters(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved filters:', error);
      setSavedFilters([]);
    }
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters: FilterState = {
      search: '',
      status: 'all',
      priority: 'all',
      type: 'all',
      assigneeId: 'all',
      clientId: 'all',
      dueDateFrom: '',
      dueDateTo: '',
      createdDateFrom: '',
      createdDateTo: '',
      tags: [],
      overdue: false,
      unassigned: false,
    };
    setFilters(clearedFilters);
  };

  const handleSaveFilter = async () => {
    if (!saveFilterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    try {
      const response = await api.post('/firm-admin/tasks/saved-filters', {
        name: saveFilterName.trim(),
        filters,
        isDefault: false
      });

      if (response.data.success) {
        toast.success('Filter saved successfully');
        setSaveFilterName('');
        setShowSaveFilter(false);
        fetchSavedFilters();
      }
    } catch (error: any) {
      console.error('Error saving filter:', error);
      toast.error('Failed to save filter');
    }
  };

  const handleLoadSavedFilter = (savedFilter: SavedFilter) => {
    setFilters(savedFilter.filters);
  };

  const handleDeleteSavedFilter = async (filterId: string) => {
    if (!confirm('Are you sure you want to delete this saved filter?')) return;

    try {
      const response = await api.delete(`/firm-admin/tasks/saved-filters/${filterId}`);
      if (response.data.success) {
        toast.success('Filter deleted successfully');
        fetchSavedFilters();
      }
    } catch (error: any) {
      console.error('Error deleting filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const addTag = (tag: string) => {
    if (tag.trim() && !filters.tags.includes(tag.trim().toLowerCase())) {
      setFilters(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim().toLowerCase()]
      }));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Advanced Filters</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Saved Filters */}
          {savedFilters.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Saved Filters</h3>
              <div className="flex flex-wrap gap-2">
                {savedFilters.map((savedFilter) => (
                  <div key={savedFilter.id} className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                    <button
                      onClick={() => handleLoadSavedFilter(savedFilter)}
                      className="text-sm text-gray-700 hover:text-primary-600"
                    >
                      {savedFilter.name}
                    </button>
                    <button
                      onClick={() => handleDeleteSavedFilter(savedFilter.id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Basic Filters */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="DRAFT">Draft</option>
                  <option value="TODO">To Do</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="REVIEW">Review</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="SUBMITTED">Submitted</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="TAX_RETURN">Tax Return</option>
                  <option value="BOOKKEEPING">Bookkeeping</option>
                  <option value="PAYROLL">Payroll</option>
                  <option value="AUDIT">Audit</option>
                  <option value="CONSULTATION">Consultation</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
                <select
                  value={filters.assigneeId}
                  onChange={(e) => setFilters(prev => ({ ...prev, assigneeId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Assignees</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                <select
                  value={filters.clientId}
                  onChange={(e) => setFilters(prev => ({ ...prev, clientId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Clients</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.displayName || client.legalName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Date Filters */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Date Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Due Date Range</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={filters.dueDateFrom}
                      onChange={(e) => setFilters(prev => ({ ...prev, dueDateFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={filters.dueDateTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, dueDateTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Created Date Range</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">From</label>
                    <input
                      type="date"
                      value={filters.createdDateFrom}
                      onChange={(e) => setFilters(prev => ({ ...prev, createdDateFrom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">To</label>
                    <input
                      type="date"
                      value={filters.createdDateTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, createdDateTo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Special Filters */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Special Filters</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.overdue}
                  onChange={(e) => setFilters(prev => ({ ...prev, overdue: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Show only overdue tasks</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.unassigned}
                  onChange={(e) => setFilters(prev => ({ ...prev, unassigned: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Show only unassigned tasks</span>
              </label>
            </div>
          </div>

          {/* Tags Filter */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {filters.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              placeholder="Add tag filter (press Enter)"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  addTag(e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Save Filter */}
          <div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowSaveFilter(!showSaveFilter)}
                className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Filter
              </button>
              {showSaveFilter && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Filter name"
                    value={saveFilterName}
                    onChange={(e) => setSaveFilterName(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleSaveFilter}
                    className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between p-6 border-t border-gray-200">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear All
          </button>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
